// ============================================================
// UandAI™ — Twilio Verify Phone OTP Login
// Resh Consultancy LLC · Vijay Anand Raj Kanaparthy
// John 14:26 CJB · PVD v3.1 · S-085 · April 11, 2026
// REPLACES: Firebase OTP (reCAPTCHA blocked all regular users)
// AUTH FLOW: Phone → /api/auth/send-otp → /api/auth/verify-otp
// OWNER BYPASS: 6466647888 · 9724003308 · 9087686436
// ============================================================

import React, { useState } from 'react';

const OWNER_PHONES = ['6466647888', '9724003308', '9087686436'];
const API_URL = process.env.REACT_APP_API_URL || 'https://api.u-and-ai.com';

function digitsOnly(phone) {
  return phone.replace(/\D/g, '');
}

function LoginScreen({ onLogin }) {
  const [step, setStep]         = useState('name_first');   // name_first | phone | otp | name
  const [phone, setPhone]       = useState('');
  const [otp, setOtp]           = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const navy  = '#080D1C';
  const gold  = '#C9A84C';
  const cream = '#F0EDE4';

  // ── Send OTP ───────────────────────────────────────────────
  async function handleSendOtp() {
    setError('');
    const digits = digitsOnly(phone);
    if (digits.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }
    setLoading(true);

    // Owner bypass — skip OTP entirely
    if (OWNER_PHONES.includes(digits)) {
      try {
        const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ phone: digits, owner_bypass: true, full_name: 'Owner' })
        });
        const data = await res.json();
        if (data.token || data.status === 'success') {
          handleLoginSuccess(data);
        } else {
          setError(data.error || 'Login failed');
        }
      } catch (e) {
        setError('Connection error. Please try again.');
      }
      setLoading(false);
      return;
    }

    // Regular user — Twilio Verify via backend
    try {
      const res = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ phone: digits })
      });
      const data = await res.json();
      if (data.status === 'sent' || data.message) {
        setStep('otp');
      } else {
        setError(data.error || 'Failed to send code. Please try again.');
      }
    } catch (e) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  }

  // ── Verify OTP ─────────────────────────────────────────────
  async function handleVerifyOtp() {
    setError('');
    if (otp.length < 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ phone: digitsOnly(phone), otp: otp, full_name: fullName.trim() })
      });
      const data = await res.json();

      if (data.token || data.status === 'success') {
        handleLoginSuccess(data);
      } else if (data.status === 'name_required') {
        setStep('name');
      } else {
        setError(data.error || 'Invalid code. Please try again.');
      }
    } catch (e) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  }

  // ── Submit name (new user) ─────────────────────────────────
  async function handleSubmitName() {
    setError('');
    if (!fullName.trim()) {
      setError('Please enter your name');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          phone: digitsOnly(phone),
          otp: otp,
          full_name: fullName.trim()
        })
      });
      const data = await res.json();
      if (data.token || data.status === 'success') {
        handleLoginSuccess(data);
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (e) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  }

  // ── Shared login success handler ───────────────────────────
  function handleLoginSuccess(data) {
    const token    = data.token || data.auth_token;
    const name     = (data.user && data.user.full_name) || data.full_name;
    const isOwner  = (data.user && data.user.tier === 'owner') || data.is_owner;
    const id       = (data.user && data.user.id) || data.id;
    const userMode = data.user_mode || data.userMode || 'developer';

    localStorage.setItem('uandai_token', token);
    localStorage.setItem('uandai_session', data.session_id || '');
    localStorage.setItem('uandai_name', name || '');
    localStorage.setItem('uandai_owner', isOwner ? 'true' : 'false');

    onLogin({
      token,
      sessionId: data.session_id,
      fullName: name,
      isOwner,
      id,
      userMode,
      user_mode: userMode
    });
  }

  // ── Styles ─────────────────────────────────────────────────
  const containerStyle = {
    minHeight: '100vh',
    background: navy,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', sans-serif",
  };

  const cardStyle = {
    background: 'rgba(255,255,255,0.04)',
    border: `1px solid ${gold}33`,
    borderRadius: '12px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '400px',
  };

  const titleStyle = {
    fontFamily: "'Cormorant Garamond', serif",
    color: gold,
    fontSize: '2rem',
    fontWeight: 600,
    marginBottom: '8px',
    textAlign: 'center',
  };

  const subtitleStyle = {
    color: cream,
    opacity: 0.6,
    fontSize: '0.85rem',
    textAlign: 'center',
    marginBottom: '32px',
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    background: 'rgba(255,255,255,0.06)',
    border: `1px solid ${gold}44`,
    borderRadius: '8px',
    color: cream,
    fontSize: '1rem',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '16px',
  };

  const btnStyle = {
    width: '100%',
    padding: '14px',
    background: gold,
    color: navy,
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1,
    marginTop: '8px',
  };

  const backStyle = {
    color: gold,
    opacity: 0.7,
    fontSize: '0.85rem',
    cursor: 'pointer',
    textAlign: 'center',
    marginTop: '16px',
    textDecoration: 'underline',
  };

  const errorStyle = {
    color: '#ff6b6b',
    fontSize: '0.85rem',
    marginBottom: '12px',
    textAlign: 'center',
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={titleStyle}>UandAI™</div>
        <div style={subtitleStyle}>The AI Listens. The Human Decides.</div>

        {error && <div style={errorStyle}>{error}</div>}

        {/* Step 1 — Full Name (new user entry point) */}
        {step === 'name_first' && (
          <>
            <div style={{ color: cream, opacity: 0.7, fontSize: '0.9rem', marginBottom: '20px', textAlign: 'center' }}>
              Welcome. What is your full name?
            </div>
            <input
              style={inputStyle}
              type="text"
              placeholder="Your full name"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (() => {
                if (fullName.trim().length >= 2) setStep('phone');
                else setError('Please enter your full name');
              })()}
              autoFocus
            />
            <button style={btnStyle} onClick={() => {
              setError('');
              if (!fullName.trim() || fullName.trim().length < 2) {
                setError('Please enter your full name');
                return;
              }
              setStep('phone');
            }} disabled={loading}>
              Continue
            </button>
            <div style={{ color: cream, opacity: 0.4, fontSize: '0.75rem', textAlign: 'center', marginTop: '16px' }}>
              John 14:26 CJB
            </div>
          </>
        )}

        {/* Step 2 — Phone */}
        {step === 'phone' && (
          <>
            <div style={{ color: cream, opacity: 0.7, fontSize: '0.9rem', marginBottom: '20px', textAlign: 'center' }}>
              Hi {fullName.split(' ')[0]}. Enter your phone number.
            </div>
            <input
              style={inputStyle}
              type="tel"
              placeholder="Phone number (e.g. 9725551234)"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
              autoFocus
            />
            <button style={btnStyle} onClick={handleSendOtp} disabled={loading}>
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
            <div style={backStyle} onClick={() => { setStep('name_first'); setPhone(''); setError(''); }}>
              ← Change name
            </div>
          </>
        )}

        {/* Step 3 — OTP */}
        {step === 'otp' && (
          <>
            <div style={{ color: cream, opacity: 0.7, fontSize: '0.9rem', marginBottom: '20px', textAlign: 'center' }}>
              Enter the 6-digit code sent to {phone}
            </div>
            <input
              style={{ ...inputStyle, letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.4rem' }}
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
              autoFocus
            />
            <button style={btnStyle} onClick={handleVerifyOtp} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <div style={backStyle} onClick={() => { setStep('phone'); setOtp(''); setError(''); }}>
              ← Change phone number
            </div>
          </>
        )}

        {/* Step 4 — Name fallback (if backend says name_required) */}
        {step === 'name' && (
          <>
            <div style={{ color: cream, opacity: 0.7, fontSize: '0.9rem', marginBottom: '20px', textAlign: 'center' }}>
              One more thing — what is your full name?
            </div>
            <input
              style={inputStyle}
              type="text"
              placeholder="Your full name"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmitName()}
              autoFocus
            />
            <button style={btnStyle} onClick={handleSubmitName} disabled={loading}>
              {loading ? 'Creating account...' : 'Get Started'}
            </button>
          </>
        )}

      </div>
    </div>
  );
}

export default LoginScreen;
